// frontend/src/components/common/AddToHomeScreenPrompt.tsx
//
// iOS Safari cannot receive web push from a normal browser tab - only from a
// site added to the Home Screen (it then opens as a PWA via manifest.json and
// can register for notifications). This dismissible prompt recovers a slice
// of iPhone web users until the native app is on the App Store.
//
// Renders ONLY when: iOS device + regular Safari tab (not standalone PWA) +
// not the native Capacitor app + not previously dismissed.

import React, { useState } from 'react';
import { Share, X } from 'lucide-react';
import { isNativePlatform } from '@/services/platform';
import { mixpanelTrack } from '@/services/mixpanel';

const DISMISS_KEY = 'a2hs_prompt_dismissed';

const isEligible = (): boolean => {
  try {
    if (isNativePlatform()) return false;
    const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent);
    if (!isIos) return false;
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      // iOS Safari's non-standard flag
      (navigator as unknown as { standalone?: boolean }).standalone === true;
    if (isStandalone) return false;
    if (localStorage.getItem(DISMISS_KEY)) return false;
    return true;
  } catch {
    return false;
  }
};

const AddToHomeScreenPrompt: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(isEligible);

  if (!visible) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // storage unavailable; session-only dismiss
    }
    mixpanelTrack('A2HS Prompt Dismissed', {});
    setVisible(false);
  };

  return (
    <div className="bg-surface-card border border-gray-700 rounded-lg p-3 flex items-start gap-3">
      <Share size={20} className="text-accent-light-blue flex-shrink-0 mt-0.5" />
      <div className="flex-grow min-w-0">
        <p className="text-sm font-semibold text-gray-100">Get contest reminders on your phone</p>
        <p className="text-xs text-gray-400 mt-0.5">
          Tap the <span className="text-gray-200 font-medium">Share</span> button below, then{' '}
          <span className="text-gray-200 font-medium">"Add to Home Screen"</span>. Open from there
          to turn on notifications.
        </p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-gray-500 hover:text-gray-300 flex-shrink-0 p-1"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default AddToHomeScreenPrompt;
